import { memo, useMemo } from 'react';
import { Chip, Tooltip, type ChipProps } from '@mui/material';
import { useVideoProcessingCheck } from 'screens/editorial/asset/components/common/FileFieldInput/hooks/useVideoProcessingCheck';
import { getVideoSourceUrl } from 'screens/editorial/asset/components/common/FileFieldInput/utility/videoUtils';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import getStatusColor from 'components/Search/utility/getStatusColor';
import type {
  HasStatus,
  RowStatus,
} from 'components/Search/components/fields/generic/StatusIndicators/types/status';
import { bloxValues, bloxColors } from 'themes/constants';
import { useCosmos } from 'providers/contexts/CosmosContext';

import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DownloadingIcon from '@mui/icons-material/Downloading';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ScheduleIcon from '@mui/icons-material/Schedule';
import type { SvgIconComponent } from '@mui/icons-material';

// Extend dayjs with UTC and timezone plugins
dayjs.extend(timezone);
dayjs.extend(utc);

// Guard for translation (revert to raw if unknown instead of translation key)
const KNOWN_STATUSES = [
  'published',
  'scheduled',
  'draft',
  'unpublished',
  'unpublished_changes',
  'processing',
  'deleted',
] as const satisfies Readonly<RowStatus[]>;

const isRowStatus = (value: string): value is RowStatus =>
  (KNOWN_STATUSES as readonly string[]).includes(value);

// Icons
export const STATUS_TO_ICON: Partial<Record<RowStatus, SvgIconComponent>> = {
  published: CheckCircleIcon,
  draft: EditNoteIcon,
  unpublished_changes: EditNoteIcon,
  processing: DownloadingIcon,
  deleted: DeleteForeverIcon,
  unpublished: BlockIcon,
  scheduled: ScheduleIcon,
};

// Guard against invalid dayjs input
type DayjsInput = string | number | Date | Dayjs;
const isDayjsInput = (v: unknown): v is DayjsInput =>
  typeof v === 'string' || typeof v === 'number' || v instanceof Date || dayjs.isDayjs(v);

export const getStartTimeInFuture = (startValue: unknown): boolean => {
  return (
    isDayjsInput(startValue) &&
    dayjs.utc(startValue).isValid() &&
    dayjs.utc(startValue).isAfter(dayjs.utc())
  ); // Now
};

// Translate status and description by status value
const getLabelKey = (status: RowStatus) => `blox.editorial.status.${status}.label`;

const getDescriptionKey = (status: RowStatus) =>
  `blox.editorial.status.${status}.description`;

export type StatusChipProps = {
  record: HasStatus;
  isOverlay?: boolean;
  iconSize?: number;
  chipVariant?: ChipProps['variant'];
  tint?: number;
  sx?: any;
  ignoreScheduled?: boolean; // If true, ignores "scheduled" pseudo-status even if start_time is in the future.
  startTimeInFutureDelayMs?: number; // ms: adds time delay for "scheduled" status for race conditions in batch upload.
  iconOnly?: boolean; // If true, shows only the icon without the label text.
};

export const StatusChip = memo(
  ({
    record,
    isOverlay = false,
    iconSize = 16,
    chipVariant = 'outlined',
    tint = 0.1,
    sx,
    ignoreScheduled = false,
    startTimeInFutureDelayMs = 0,
    iconOnly = false,
  }: StatusChipProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { sessionConfig } = useCosmos();
    const siteTimeZone = sessionConfig?.time_zone;

    // Derive video source URL from record for processing check.
    const videoSourceUrl = useMemo(
      () =>
        record.type === 'video'
          ? getVideoSourceUrl({
              filesVideoUrl: record.files?.video?.url,
              remoteVideoPlayer: record.remote_video_player,
              remoteVideoMp4Url: record.remote_video?.properties?.mp4_url,
              remoteVideoUrl: record.remote_video?.properties?.url,
            })
          : undefined,
      [
        record.type,
        record.files?.video?.url,
        record.remote_video_player,
        record.remote_video?.properties?.mp4_url,
        record.remote_video?.properties?.url,
      ],
    );

    const isVideoProcessing = useVideoProcessingCheck(videoSourceUrl);

    const rawStatus = String(record.status ?? '').toLowerCase();
    const startValue = record.start_time;
    const deleteTime = record.delete_time;
    const draft = record.draft;
    const noPublish = record?.no_publish ?? record.properties?.no_publish;

    // delete_time set in the past/now means the asset is effectively deleted
    const isEffectivelyDeleted =
      isDayjsInput(deleteTime) &&
      dayjs.utc(deleteTime).isValid() &&
      !dayjs.utc(deleteTime).isAfter(dayjs.utc());

    const startTimeInFuture =
      isDayjsInput(startValue) &&
      dayjs(startValue).isValid() &&
      dayjs
        .utc(startValue)
        .tz(siteTimeZone)
        .isAfter(dayjs().tz(siteTimeZone).add(startTimeInFutureDelayMs, 'ms'));

    // Scheduled pseudo-status applies whenever start_time is in the future
    // and ignoreScheduled is not set. Used in both the inferred and explicit status paths.
    const isScheduled = startTimeInFuture && !ignoreScheduled;

    let computedStatus = rawStatus;

    // If no status, infer from fields
    if (!rawStatus) {
      if (draft) {
        computedStatus = 'draft';
      } else if (noPublish) {
        computedStatus = 'unpublished';
      } else if (isScheduled) {
        // Only applies when the asset would be published (not draft, not noPublish).
        computedStatus = 'scheduled';
      } else {
        computedStatus = 'published';
      }
    } else {
      // Status is directly provided - Adjust for scheduled pseudo-status
      const isPublishableUnpublished =
        rawStatus === 'unpublished' && !noPublish && !draft;
      computedStatus =
        (rawStatus === 'published' || isPublishableUnpublished) && isScheduled
          ? 'scheduled'
          : rawStatus;
    }

    // Deleted takes highest priority - overrides everything
    if (isEffectivelyDeleted) {
      computedStatus = 'deleted';
    }
    // Video processing takes precedence next over all other computed status
    else if (isVideoProcessing) {
      computedStatus = 'processing';
    }

    // "Unpublished Changes" status - asset has pending draft changes but is "published"
    // Applies when draft is true and no_publish is falsy
    if (
      draft &&
      !noPublish &&
      computedStatus !== 'deleted' &&
      computedStatus !== 'processing'
    ) {
      computedStatus = 'unpublished_changes';
    }

    const isKnown = isRowStatus(computedStatus);
    const effectiveStatus = isKnown ? (computedStatus as RowStatus) : undefined;

    const color = isKnown
      ? getStatusColor({ status: effectiveStatus! }, theme)
      : theme.palette.text.secondary;

    const Icon = isKnown ? STATUS_TO_ICON[effectiveStatus!] : undefined;

    const chipLabel = isKnown ? t(getLabelKey(effectiveStatus!)) : computedStatus;
    const tooltipTitle = isKnown
      ? effectiveStatus === 'scheduled' && isDayjsInput(startValue)
        ? t('blox.editorial.status.scheduled.scheduled_date', {
            date: dayjs.utc(startValue).tz(siteTimeZone).format('MMM D, YYYY h:mm A'),
          })
        : t(getDescriptionKey(effectiveStatus!))
      : computedStatus;

    if (iconOnly) {
      return (
        <Tooltip
          title={tooltipTitle}
          placement="top"
        >
          <span
            role="status"
            aria-label={chipLabel}
            data-testid="status-field"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color,
            }}
          >
            {Icon ? <Icon sx={{ fontSize: iconSize, ...sx }} /> : null}
          </span>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        title={tooltipTitle}
        placement="top"
      >
        <Chip
          role="status"
          aria-label={chipLabel}
          component="span"
          icon={Icon ? <Icon sx={{ fontSize: iconSize }} /> : undefined}
          label={chipLabel}
          size="small"
          variant={chipVariant}
          sx={{
            px: 0.5,
            fontSize: bloxValues.smallFontSizeRem,
            textTransform: 'capitalize',
            fontWeight: theme.typography.fontWeightMedium,
            color,
            borderColor: color,
            '.MuiChip-icon': { color: 'inherit' },
            backgroundColor: isOverlay
              ? alpha(bloxColors.white, 0.9)
              : chipVariant === 'filled'
                ? alpha(color, tint)
                : undefined,
            ...sx,
          }}
          data-testid="status-field"
        />
      </Tooltip>
    );
  },
);
StatusChip.displayName = 'StatusChip';
